// DQ Math 05 (Invalid Values & Rule Violations) never had its promised Learner
// Practice tables written — the solved-examples file ends with "Learner
// Practice Starts Below" and nothing after it. These 8 items are fresh,
// scenario-based workplace cases mirroring each of the file's 8 Solved
// Examples (same rule family, same lane, different company/data), matching
// the "Mixed DIY Practice Cases" style used in the Statistics Clinic practice
// files: a real business trigger, not just a bare table. Every answer is
// computed directly from the scenario's own data, not a placeholder.

export const DQ_MATH_05_TRACKER_ID = 'dq-math-05-invalid-values-practice-bank';

export const DQ_MATH_05_LENSES = [
  ['detection_rule', 'Detection rule'],
  ['evidence', 'Evidence'],
  ['business_check', 'Business check'],
  ['root_cause', 'Root cause'],
  ['treatment', 'Treatment'],
  ['caution', 'Caution'],
  ['verify', 'Verify'],
  ['learner_sentence', 'Learner sentence'],
];

const WORKING_PROMPTS = {
  V1: 'Name the lane (single-value), state the range rule for age, and show which row fails it.',
  V2: 'Name the lane (single-value), attempt to parse every value as a number, and compute the type-mismatch rate.',
  V3: 'Name the lane (single-value), apply a phone-number format check (11 digits starting 07), and compute the violation rate.',
  V4: 'Name the lane (single-value), compute the enumeration violation rate against the approved tier list, and check whether the unlisted value is one consistent string or scattered variants.',
  V5: 'Name the lane (relational, same row), state the cross-field rule, and check every row against it.',
  V6: 'Name the lane (relational, same row), state the cross-field rule, and check every row against it.',
  V7: 'Name the lane (relational, cross-table), compute the orphan rate via a left join, and check whether orphans cluster around one event.',
  V8: 'Name the lane for EACH row separately — this table mixes single-value, cross-field, and cross-table violations in one capstone.',
};

export const DQ_MATH_05_ITEMS = [
  {
    id: 'V1',
    title: 'DIY Practice V1: Workforce report sign-off',
    scenario: "Northgate Manufacturing's HR team is preparing a workforce-planning report ahead of next year's retirement wave. Before the numbers go into the report, you've been asked to check the age field on a small batch of employee records pulled from the HRIS export.",
    tables: [{
      columns: ['employee_id', 'age'],
      rows: [
        ['E001', '29'],
        ['E002', '41'],
        ['E003', '-3'],
        ['E004', '55'],
      ],
    }],
    answerGuide: {
      detection_rule: 'Range rule: 0 <= age <= 120; violation rate = 1/4 = 25% (E003).',
      evidence: 'E003, age = -3.',
      business_check: 'No plausible correction is knowable from this table alone; the true age must come from the HR source record.',
      root_cause: 'Family A — Domain/range violation (hard rule).',
      treatment: 'Quarantine E003 from any age-based analysis until the HR source record is checked.',
      caution: 'Do not recode -3 to a "plausible" positive age without source evidence — that invents data.',
      verify: 'Recompute the age range violation rate before and after the source check; confirm E003 either gets a confirmed value or stays quarantined.',
      learner_sentence: 'A negative age is impossible regardless of what the real value turns out to be, so it is held out of analysis until confirmed.',
    },
    ruleTerms: {
      detection_rule: ['1/4', '25%', 'range', '0', '120'],
      evidence: ['e003', '-3'],
      business_check: ['hr', 'source record', 'signup form'],
      root_cause: ['domain', 'range', 'family a'],
      treatment: ['quarantine', 'hold', 'source record'],
      caution: ['do not recode', 'invent', 'plausible'],
      verify: ['recompute', 'rate', 'confirmed'],
      learner_sentence: ['negative', 'impossible'],
    },
    criticalTerms: { evidence: ['E003'], learner_sentence: ['age'] },
  },
  {
    id: 'V2',
    title: 'DIY Practice V2: Cycle count sign-off',
    scenario: "Bramwell Distribution's operations manager wants a stock-accuracy sign-off before the weekend cycle count. A junior analyst pulled a sample from the warehouse system, and you've been asked to confirm the stock_count field is usable before it's compared against the physical count.",
    tables: [{
      columns: ['item_id', 'stock_count'],
      rows: [
        ['W001', '15'],
        ['W002', 'N/A'],
        ['W003', '22'],
        ['W004', 'nine'],
        ['W005', '8'],
        ['W006', '30'],
      ],
    }],
    answerGuide: {
      detection_rule: 'Type-cast check: attempt to parse stock_count as a number; type-mismatch rate = 2/6 = 33.3%.',
      evidence: 'W002 = "N/A"; W004 = "nine".',
      business_check: '"N/A" is a placeholder pattern more typical of missing-data handling than a genuine count; "nine" looks like a manual entry error where a number was expected.',
      root_cause: 'Family A — Type mismatch, two different underlying causes in the same column.',
      treatment: 'Recode "N/A" following the missing-data standard rather than the validity standard — it is a placeholder, not a true type error. Correct "nine" to 9 only if the source stock sheet confirms it; otherwise quarantine.',
      caution: 'Do not treat both failures the same way — one belongs to the missing-data workflow, the other to a source-correction workflow.',
      verify: 'Confirm the recoded/corrected stock_count column parses as numeric for all rows; recompute the type-mismatch rate.',
      learner_sentence: 'Two rows fail the same type check for different reasons — one is really a missing value in disguise, the other a genuine entry error — and they need different fixes.',
    },
    ruleTerms: {
      detection_rule: ['2/6', '33.3%', 'type', 'parse'],
      evidence: ['w002', 'w004', 'n/a', 'nine'],
      business_check: ['placeholder', 'missing', 'entry error'],
      root_cause: ['type mismatch', 'family a'],
      treatment: ['recode', 'missing-data standard', 'quarantine', 'confirm'],
      caution: ['different', 'do not treat the same'],
      verify: ['recompute', 'parses', 'numeric'],
      learner_sentence: ['disguise', 'entry error'],
    },
    criticalTerms: { evidence: ['N/A', 'nine'], learner_sentence: [] },
  },
  {
    id: 'V3',
    title: 'DIY Practice V3: Billing reminder launch',
    scenario: "Solstice Mobile's customer service team is about to launch an SMS billing-reminder campaign. Before the marketing platform can send anything, you've been asked to validate a sample of customer phone numbers pulled from the CRM export.",
    tables: [{
      columns: ['customer_id', 'phone'],
      rows: [
        ['C301', '07911 123456'],
        ['C302', '07700-222333'],
        ['C303', '0791@123456'],
        ['C304', '07911'],
        ['C305', '07123 456789'],
        ['C306', '07555 987654'],
      ],
    }],
    answerGuide: {
      detection_rule: 'Format check: 11 digits starting with 07 (spaces/dashes stripped); violation rate = 2/6 = 33.3%.',
      evidence: 'C303 = "0791@123456" (contains a non-digit character); C304 = "07911" (only 5 digits).',
      business_check: 'C303 looks like a single stray-character entry error; C304 looks like a severely truncated or incomplete entry.',
      root_cause: 'Family A — Format violation.',
      treatment: 'Flag both for correction; do not guess the missing digits for C304 without checking the source signup record.',
      caution: 'Do not silently strip the "@" from C303 and call it fixed — confirm against the source before treating this as resolved.',
      verify: 'Recompute the format violation rate after source confirmation; confirm both rows either pass or remain visibly flagged.',
      learner_sentence: 'A third of these phone numbers fail a basic format check, and each one needs its own source check before correcting it.',
    },
    ruleTerms: {
      detection_rule: ['2/6', '33.3%', '11 digit', 'format'],
      evidence: ['c303', 'c304', '@', '5 digit'],
      business_check: ['stray', 'truncated', 'incomplete'],
      root_cause: ['format', 'family a'],
      treatment: ['flag', 'source', 'signup record'],
      caution: ['do not strip', 'confirm'],
      verify: ['recompute', 'format violation rate'],
      learner_sentence: ['third', 'source check'],
    },
    criticalTerms: { evidence: ['C303', 'C304'] },
  },
  {
    id: 'V4',
    title: 'DIY Practice V4: Quarter-close tier check',
    scenario: "Vantage Analytics' finance team is closing the books for the quarter and needs every subscription tier to match an approved list before revenue is recognised. Billing flagged an unusually high volume of one specific tier value and asked you to investigate before month-end close.",
    tables: [{
      columns: ['approved_tiers'],
      rows: [['free'], ['basic'], ['premium'], ['enterprise']],
    }, {
      columns: ['sample_note'],
      rows: [['Of 3,000 subscription rows, 210 have tier = "trial_plus", which does not appear on the approved list above.']],
    }],
    answerGuide: {
      detection_rule: 'Enumeration check against the approved list {free, basic, premium, enterprise}; violation rate = 210/3,000 = 7.0%, entirely one consistent value.',
      evidence: '210 rows with tier = "trial_plus", none matching the approved list.',
      business_check: 'A single, consistently-spelled unlisted value used 210 times is far more consistent with an intentionally introduced new tier than with independent data-entry typos.',
      root_cause: 'Family A — Enumeration violation, likely a governance gap rather than a data error.',
      treatment: 'Escalate to the product/business owner to confirm whether "trial_plus" is a legitimate new tier; if confirmed, update the approved list rather than treating the rows as invalid.',
      caution: 'Do not silently recode "trial_plus" to the nearest approved value — that would misrepresent 210 real subscriptions.',
      verify: 'After the list is updated (or the rows are corrected, if "trial_plus" turns out to be a genuine error), confirm the enumeration violation rate returns to 0%.',
      learner_sentence: '210 subscriptions share one consistent, unlisted tier — that looks like a missing category, not 210 typos, so the fix is updating the rulebook, not the data.',
    },
    ruleTerms: {
      detection_rule: ['210', '7.0%', '3,000', 'enumeration'],
      evidence: ['trial_plus', '210'],
      business_check: ['consistent', 'new tier', 'typo'],
      root_cause: ['enumeration', 'governance', 'family a'],
      treatment: ['escalate', 'business owner', 'update the list'],
      caution: ['do not recode', 'misrepresent'],
      verify: ['0%', 'list is updated'],
      learner_sentence: ['missing category', 'not', 'typos'],
    },
    criticalTerms: { evidence: ['trial_plus'], learner_sentence: [] },
    fallbackMinLength: { evidence: 10 },
  },
  {
    id: 'V5',
    title: 'DIY Practice V5: Pre-audit date check',
    scenario: "Fenwick Construction's PMO is auditing the project tracker before a client audit next week. You've been asked to check that no project's dates are logically impossible before the tracker is shared externally.",
    tables: [{
      columns: ['project_id', 'start_date', 'end_date'],
      rows: [
        ['P701', '2026-08-01', '2026-07-30'],
        ['P702', '2026-08-02', '2026-08-10'],
        ['P703', '2026-08-03', '2026-08-03'],
      ],
    }],
    answerGuide: {
      detection_rule: 'Cross-field rule: end_date >= start_date.',
      evidence: 'P701 — start_date 2026-08-01, end_date 2026-07-30.',
      business_check: 'Both dates are individually well-formed and plausible; the contradiction only exists between them — a physically impossible business sequence (ended before it started).',
      root_cause: 'Family B — Business rule violation (hard rule).',
      treatment: 'Quarantine P701 from any duration metric until the source system confirms which date is correct.',
      caution: 'Do not assume start_date is the wrong one just because entry errors are more common there — check the source before deciding which field to trust.',
      verify: 'Recompute the business-rule violation rate before and after the source check; confirm P701 either gets a corrected date or stays quarantined.',
      learner_sentence: 'This project supposedly ended two days before it started — a hard contradiction that has to be resolved at the source before it is used in any duration metric.',
    },
    ruleTerms: {
      detection_rule: ['end_date', 'start_date', '>='],
      evidence: ['p701', '2026-08-01', '2026-07-30'],
      business_check: ['well-formed', 'contradiction', 'impossible'],
      root_cause: ['business rule', 'family b'],
      treatment: ['quarantine', 'source system'],
      caution: ['do not assume', 'check the source'],
      verify: ['recompute', 'business-rule violation rate'],
      learner_sentence: ['ended before', 'contradiction'],
    },
    criticalTerms: { evidence: ['P701'] },
  },
  {
    id: 'V6',
    title: 'DIY Practice V6: Expense run review',
    scenario: "Kestrel Consulting's finance team is reviewing this month's travel expense claims before the reimbursement run. You've been asked to flag any claim that doesn't respect its approved budget before payments go out.",
    tables: [{
      columns: ['claim_id', 'approved_budget', 'claimed_amount'],
      rows: [
        ['X801', '500.00', '500.00'],
        ['X802', '300.00', '620.00'],
        ['X803', '800.00', '450.00'],
      ],
    }],
    answerGuide: {
      detection_rule: 'Cross-field rule: claimed_amount <= approved_budget.',
      evidence: 'X802 — approved_budget 300.00, claimed_amount 620.00.',
      business_check: 'Both amounts are individually valid currency values; the contradiction is that more was claimed than was ever approved, which should be impossible under normal expense policy.',
      root_cause: 'Family B — Cross-field logical violation (hard rule).',
      treatment: 'Quarantine X802 from any expense-total reporting; investigate whether this reflects an approved budget increase that was miscoded, or a genuine overspend.',
      caution: 'Do not simply cap claimed_amount at 300.00 to force the rule to pass — that could hide a legitimately approved budget increase that was wrongly recorded.',
      verify: 'Confirm the true nature of X802 against the approval record; recompute total claims with and without X802 to show the reporting impact.',
      learner_sentence: 'This claim is more than double the approved budget — that needs an explanation before it is counted as a normal claim in any total.',
    },
    ruleTerms: {
      detection_rule: ['claimed_amount', 'approved_budget', '<='],
      evidence: ['x802', '300', '620'],
      business_check: ['valid currency', 'contradiction', 'expense policy'],
      root_cause: ['cross-field', 'family b'],
      treatment: ['quarantine', 'investigate', 'miscoded'],
      caution: ['do not cap', 'hide'],
      verify: ['approval record', 'recompute'],
      learner_sentence: ['double', 'explanation'],
    },
    criticalTerms: { evidence: ['X802'] },
  },
  {
    id: 'V7',
    title: 'DIY Practice V7: Post-migration review check',
    scenario: "Meridian Retail recently migrated its product catalog to a new platform. The customer experience team wants to know whether any product reviews are now orphaned before the new review widget goes live on the site.",
    tables: [{
      columns: ['sample_note'],
      rows: [['Reviews table (2,000 rows, product_id populated on all). Products table has 1,940 unique product_id values. A left join from reviews to products, matching on product_id, produces 28 rows with no matching product record, all clustered around one catalog-migration date.']],
    }],
    answerGuide: {
      detection_rule: 'Referential check via left join: reviews.product_id against products.product_id; orphan rate = 28/2,000 = 1.4%.',
      evidence: '28 reviews reference a product_id with no matching row in the products table, clustered around one catalog-migration date.',
      business_check: 'The clustering pattern points to a batch of product records that failed to migrate, rather than 28 independent, scattered entry errors.',
      root_cause: 'Family C — Referential rule violation (hard rule for any product-level reporting).',
      treatment: 'Quarantine the 28 reviews from product-level reporting; investigate the migration batch for the missing product records and backfill if recoverable.',
      caution: 'Do not drop the 28 reviews from overall review-volume totals just because they cannot be attributed to a product — the reviews themselves may still be real; only the product attribution is broken.',
      verify: 'After the migration investigation, recompute the orphan rate; confirm it drops toward 0% once the missing product records are restored, or the reviews remain visibly flagged if unrecoverable.',
      learner_sentence: 'About 1.4% of reviews point to a product we cannot find, clustered around one migration date — the fix is recovering that batch, not treating each review as a separate error.',
    },
    ruleTerms: {
      detection_rule: ['28', '2,000', '1.4%', 'left join', 'referential'],
      evidence: ['28', 'migration', 'product_id'],
      business_check: ['clustering', 'batch', 'migrate'],
      root_cause: ['referential', 'family c'],
      treatment: ['quarantine', 'backfill', 'investigate'],
      caution: ['do not drop', 'reviews', 'still real'],
      verify: ['recompute', 'orphan rate'],
      learner_sentence: ['1.4%', 'migration date'],
    },
    criticalTerms: { evidence: ['28'] },
  },
  {
    id: 'V8',
    title: 'DIY Practice V8: Subscription pipeline sweep',
    scenario: "Vantage Analytics' data quality lead wants a single validity sweep across today's new subscription records before they flow into the billing pipeline. This batch mixes several different rule types in one table, so each row needs to be diagnosed on its own terms — not one shared answer.",
    tables: [{
      columns: ['sub_id', 'start_date', 'end_date', 'seats', 'tier', 'customer_id'],
      rows: [
        ['S901', '2026-08-01', '2026-08-31', '5', 'premium', 'CU01'],
        ['S902', '2026-08-02', '2026-07-30', '3', 'basic', 'CU02'],
        ['S903', '2026-08-03', '2026-09-03', '-2', 'premium', 'CU03'],
        ['S904', '2026-08-04', '2026-09-04', '4', 'trial_plus', 'CU04'],
        ['S905', '2026-08-05', '2026-09-05', '2', 'basic', 'CU999'],
      ],
    }],
    answerGuide: {
      detection_rule: 'S902 — end_date >= start_date rule; S903 — seats range rule (must be positive); S904 — tier enumeration rule; S905 — referential check against the customers table.',
      evidence: 'S902 ends before it starts (2026-07-30 < 2026-08-02); S903 has seats = -2; S904 has tier = "trial_plus" (a single occurrence, unlike V4\'s 210-row pattern); S905\'s customer_id CU999 has no match in customers.',
      business_check: 'S902 and S903 are both physically impossible regardless of context. S904 is a single occurrence, so there is not yet strong evidence for a legitimate new tier the way there was in V4. S905 needs the customer table checked before concluding whether CU999 is a genuine orphan or a typo of a real ID.',
      root_cause: 'S902 = Family B (business rule violation); S903 = Family A (domain/range violation); S904 = Family A (enumeration violation, unresolved pending evidence); S905 = Family C (referential rule violation, pending confirmation).',
      treatment: 'S902 — quarantine, check source dates. S903 — quarantine, check source seat count. S904 — flag for business review rather than assuming either "typo" or "new tier" from one row alone. S905 — quarantine from customer-level reporting; check whether CU999 is a near-miss typo of a real customer_id.',
      caution: 'Do not resolve S904 the same way as V4\'s 210-row case — one occurrence is not enough evidence to conclude it is a legitimate new tier; and do not assume S905 is definitely a migration artifact without checking, since a single orphan is also consistent with a simple typo.',
      verify: 'Recompute each rule\'s violation rate after investigation; confirm S901 continues to pass all checks as the baseline "clean" row for comparison.',
      learner_sentence: 'Four different rows fail four different rules here, and the amount of evidence available — a 210-row pattern versus a single occurrence — genuinely changes how confident the conclusion can be, not just which rule applies.',
    },
    ruleTerms: {
      detection_rule: ['s902', 's903', 's904', 's905'],
      evidence: ['s902', 's903', 's904', 's905', 'cu999'],
      business_check: ['single occurrence', 'evidence', 'check'],
      root_cause: ['family a', 'family b', 'family c'],
      treatment: ['quarantine', 'flag', 'check'],
      caution: ['do not resolve', 'do not assume'],
      verify: ['s901', 'baseline', 'clean row'],
      learner_sentence: ['four', 'different rows', 'confidence'],
    },
    criticalTerms: { evidence: ['S902', 'S903', 'S904', 'S905'] },
    fallbackMinLength: { detection_rule: 20, root_cause: 20, treatment: 20 },
  },
];

export const DQ_MATH_05_CONFIG = {
  trackerId: DQ_MATH_05_TRACKER_ID,
  resourceKey: 'dq-math-05-invalid-values-rule-violations-solved-examples',
  rubricKey: 'data_quality_invalid_values',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Invalid Values Tables',
  description: 'Name the lane (single-value, cross-field, or cross-table), show your working first, then decide the rule, root cause, and treatment. Your work autosaves to your own practice record.',
  introAlert: 'Start each table by naming the lane: single-value (checkable from one field alone), cross-field (needs a second field in the same row), or cross-table (needs a lookup elsewhere). Then show the actual rule check and rate before you classify the root cause.',
  lenses: DQ_MATH_05_LENSES,
  workingField: {
    key: 'approach_working',
    label: 'Approach / working',
    prompts: WORKING_PROMPTS,
    placeholder: 'Start with: Lane: [Single-value / Relational] because... Then show the rule check, the rate, and which row(s) fail it...',
  },
  items: DQ_MATH_05_ITEMS,
};
